import React, { useState } from 'react';
import { ClipboardPen } from 'lucide-react';
import AppointmentModal from './AppointmentModal';

/**
 * FloatingAppointmentButton
 * ─────────────────────────────────────────────────────────────────────────────
 * Premium fixed circular floating button that opens the appointment modal.
 *
 * Stacking (bottom-right corner):
 *   WhatsApp pill  — bottom-6  right-6  (24px from bottom/right)
 *   This button    — bottom-24 right-6  (96px from bottom, same right)
 *   Gap between them ≈ 20px (96 - 24 - pill height ~52px)
 *
 * Desktop: circular button + "Book Appointment" tooltip on left side
 * Mobile:  circular button only, no tooltip
 * ─────────────────────────────────────────────────────────────────────────────
 */
const FloatingAppointmentButton = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [hovered, setHovered]     = useState(false);

  return (
    <>
      {/* ── Floating button + tooltip wrapper ──────────────────────────── */}
      <div
        className="fixed bottom-24 right-6 z-50 flex items-center"
        aria-hidden={modalOpen}
      >
        {/* Tooltip — desktop only, fades in on hover */}
        <div
          aria-hidden="true"
          className={[
            'hidden sm:flex mr-3 items-center whitespace-nowrap relative',
            // Appearance
            'bg-gray-900 text-white text-xs font-semibold',
            'px-3 py-1.5 rounded-lg shadow-lg pointer-events-none',
            // Fade + slide animation
            'transition-all duration-200',
            hovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2',
          ].join(' ')}
        >
          Book Appointment
          {/* Right-pointing caret */}
          <span className="absolute -right-[5px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[5px] border-l-gray-900" />
        </div>

        {/* Button */}
        <button
          onClick={() => setModalOpen(true)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          aria-label="Book Appointment"
          className={[
            // Shape & size
            'w-14 h-14 rounded-full flex items-center justify-center shrink-0',
            // Colour & border
            'bg-[#800000] border border-white/20 text-white',
            // Shadow
            'shadow-2xl',
            // Interactivity
            'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2',
            // Smooth transitions
            'transition-all duration-300',
            // Hover effect (scale + lift + darker bg)
            hovered
              ? 'bg-black scale-[1.08] shadow-[0_20px_40px_rgba(0,0,0,0.4)] -translate-y-0.5'
              : '',
          ].join(' ')}
        >
          <ClipboardPen className="w-6 h-6" />
        </button>
      </div>

      {/* ── Modal (rendered at component root, above everything) ─────────── */}
      <AppointmentModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default FloatingAppointmentButton;
